import React, { useEffect, useState } from 'react';
import { getTradovateHealth, getTrades, getDailyPnl, getCopyTradingStatus, activateCopyTrading, deactivateCopyTrading } from '../api';
import { CheckCircle, XCircle, Loader2, ChevronDown, ChevronUp } from 'lucide-react';

// ---------------------------------------------------------------------------
// Tier definitions
// ---------------------------------------------------------------------------
interface Tier {
  tier: number;
  label: string;
  minBalance: number;
  maxBalance: number | null;
  contracts: string;
  instrument: string;
  note?: string;
}

const TIERS: Tier[] = [
  { tier: 1, label: 'Tier 1', minBalance: 0, maxBalance: 500, instrument: 'MNQ only', contracts: '1 contract max' },
  { tier: 2, label: 'Tier 2', minBalance: 500, maxBalance: 1000, instrument: 'MNQ only', contracts: 'Up to 2 contracts' },
  { tier: 3, label: 'Tier 3', minBalance: 1000, maxBalance: 2500, instrument: 'MNQ', contracts: 'Up to 4 contracts', note: 'OR 1 NQ if win rate > 60% over 20+ trades' },
  { tier: 4, label: 'Tier 4', minBalance: 2500, maxBalance: 5000, instrument: 'MNQ / NQ', contracts: '1 NQ eligible, MNQ up to 5' },
  { tier: 5, label: 'Tier 5', minBalance: 5000, maxBalance: null, instrument: 'MNQ / NQ', contracts: 'Up to 2 NQ, MNQ proportional' },
];

function getActiveTier(balance: number): number {
  for (let i = TIERS.length - 1; i >= 0; i--) {
    if (balance >= TIERS[i].minBalance) return TIERS[i].tier;
  }
  return 1;
}

function GateCheck({ pass, label }: { pass: boolean; label: string }) {
  return (
    <div className="flex items-center gap-2 text-sm">
      {pass ? <CheckCircle size={14} className="text-green-400 flex-shrink-0" /> : <XCircle size={14} className="text-red-400 flex-shrink-0" />}
      <span className={pass ? 'text-gray-300' : 'text-gray-500'}>{label}</span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Contract Scaling Section
// ---------------------------------------------------------------------------
export function ContractScalingSection() {
  const [balance, setBalance] = useState<number | null>(null);
  const [winRate, setWinRate] = useState<number | null>(null);
  const [maxStreak, setMaxStreak] = useState<number>(0);
  const [maxDailyDdPct, setMaxDailyDdPct] = useState<number>(0);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);

  const load = async () => {
    try {
      const [health, tradesResp, pnlResp] = await Promise.all([
        getTradovateHealth(),
        getTrades(),
        getDailyPnl(),
      ]);

      const bal = health.data?.cash_balance ?? null;
      setBalance(bal);

      const closed = (tradesResp.data?.data || tradesResp.data || []).filter((t: any) => t.status === 'closed');
      const last20 = closed.slice(-20);
      const wins20 = last20.filter((t: any) => (t.pnl || 0) > 0).length;
      setWinRate(last20.length > 0 ? (wins20 / last20.length) * 100 : null);

      // Losing streak
      let streak = 0, maxSt = 0, cur = 0;
      closed.forEach((t: any) => {
        if ((t.pnl || 0) <= 0) { cur++; if (cur > maxSt) maxSt = cur; } else cur = 0;
      });
      setMaxStreak(maxSt);

      // Max daily drawdown
      const daily = (pnlResp.data?.data || pnlResp.data || []);
      const last7 = daily.slice(0, 7);
      let maxDd = 0;
      if (bal && bal > 0) {
        last7.forEach((d: any) => {
          const pnl = parseFloat(d.net_pnl || 0);
          if (pnl < 0) {
            const pct = (Math.abs(pnl) / bal) * 100;
            if (pct > maxDd) maxDd = pct;
          }
        });
      }
      setMaxDailyDdPct(maxDd);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); const iv = setInterval(load, 30000); return () => clearInterval(iv); }, []);

  const activeTier = balance !== null ? getActiveTier(balance) : null;
  const wr55 = winRate !== null && winRate > 55;
  const noLongStreak = maxStreak <= 4;
  const ddOk = maxDailyDdPct < 15;

  return (
    <div className="bg-gray-900 rounded-lg border border-gray-800">
      <button onClick={() => setOpen(o => !o)} className="w-full p-4 flex items-center justify-between hover:bg-gray-800 transition-colors">
        <div className="text-left">
          <h3 className="font-semibold text-sm uppercase tracking-wide text-gray-400">Contract Scaling Criteria</h3>
          <p className="text-xs text-gray-500 mt-0.5">
            {balance !== null ? `Tier ${activeTier ?? '?'} active — $${balance.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : 'Fetching balance...'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {loading && <Loader2 size={14} className="text-gray-500 animate-spin" />}
          {open ? <ChevronUp size={16} className="text-gray-500" /> : <ChevronDown size={16} className="text-gray-500" />}
        </div>
      </button>

      {open && <div className="divide-y divide-gray-800 border-t border-gray-800">
        {TIERS.map((t) => {
          const isActive = activeTier === t.tier;
          return (
            <div key={t.tier} className={`flex items-start justify-between p-4 transition-colors ${isActive ? 'bg-blue-900/20 border-l-2 border-blue-500' : 'hover:bg-gray-800/40'}`}>
              <div className="flex items-start gap-3">
                <div className={`text-xs px-2 py-0.5 rounded font-bold border mt-0.5 ${isActive ? 'bg-blue-700 border-blue-500 text-white' : 'bg-gray-800 border-gray-700 text-gray-400'}`}>
                  {t.label}
                </div>
                <div>
                  <div className={`text-sm font-medium ${isActive ? 'text-white' : 'text-gray-400'}`}>
                    {t.instrument} &mdash; {t.contracts}
                  </div>
                  {t.note && <div className="text-xs text-gray-500 mt-0.5">{t.note}</div>}
                </div>
              </div>
              <div className={`text-xs font-mono ml-4 flex-shrink-0 ${isActive ? 'text-blue-300 font-bold' : 'text-gray-600'}`}>
                {t.maxBalance ? `$${t.minBalance.toLocaleString()}–$${t.maxBalance.toLocaleString()}` : `$${t.minBalance.toLocaleString()}+`}
                {isActive && ' ◀ ACTIVE'}
              </div>
            </div>
          );
        })}
      </div>}

      {open && <div className="p-4 border-t border-gray-800">
        <div className="text-xs text-gray-500 uppercase tracking-wide mb-3">Secondary Gates</div>
        <div className="space-y-2">
          <GateCheck pass={wr55} label={`Win rate > 55% over last 20 trades (current: ${winRate !== null ? winRate.toFixed(1) + '%' : 'N/A'})`} />
          <GateCheck pass={noLongStreak} label={`No losing streak > 4 consecutive (max streak: ${maxStreak})`} />
          <GateCheck pass={ddOk} label={`Max daily drawdown < 15% of balance (worst 7d: ${maxDailyDdPct.toFixed(1)}%)`} />
        </div>
      </div>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Copy Trading Activation Section
// ---------------------------------------------------------------------------
interface CopyCondition {
  pass: boolean;
  value: any;
  label: string;
  trades_sampled?: number;
}

interface CopyStatus {
  conditions: Record<string, CopyCondition>;
  all_green: boolean;
  copy_trading_active: boolean;
  copy_account_id: string;
}

const CONDITION_ORDER = [
  'min_10_wins',
  'win_rate_60',
  'no_losing_streak',
  'net_pnl_positive',
  'no_daily_dd_10pct',
  'no_unplanned_restarts',
];

function conditionValueLabel(key: string, cond: CopyCondition): string {
  switch (key) {
    case 'min_10_wins': return `${cond.value} confirmed wins`;
    case 'win_rate_60': return `${cond.value}% over last ${cond.trades_sampled ?? 20} trades`;
    case 'no_losing_streak': return `Current streak: ${cond.value ?? 0}`;
    case 'net_pnl_positive': return `Net P&L: $${typeof cond.value === 'number' ? cond.value.toFixed(2) : 'N/A'}`;
    case 'no_daily_dd_10pct': return `Worst day: ${cond.value ?? 0}% drawdown`;
    case 'no_unplanned_restarts': return cond.value !== null ? `${cond.value} restarts (delta)` : 'Unavailable';
    default: return String(cond.value ?? '');
  }
}

export function CopyTradingSection() {
  const [status, setStatus] = useState<CopyStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [activating, setActivating] = useState(false);
  const [open, setOpen] = useState(false);

  const load = async () => {
    try {
      const resp = await getCopyTradingStatus();
      setStatus(resp.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); const iv = setInterval(load, 30000); return () => clearInterval(iv); }, []);

  const handleToggle = async () => {
    if (!status) return;
    const targetActive = !status.copy_trading_active;
    if (targetActive && !status.all_green) return;
    if (!window.confirm(targetActive ? 'Activate copy trading to subaccount 1877055?' : 'Pause copy trading for subaccount 1877055?')) return;
    setActivating(true);
    try {
      if (targetActive) {
        await activateCopyTrading(status.copy_account_id);
      } else {
        await deactivateCopyTrading(status.copy_account_id);
      }
      await load();
    } catch (e) {
      alert('Failed to update copy trading state');
    } finally {
      setActivating(false);
    }
  };

  const allGreen = status?.all_green ?? false;
  const isActive = status?.copy_trading_active ?? false;

  return (
    <div className="bg-gray-900 rounded-lg border border-gray-800">
      <button onClick={() => setOpen(o => !o)} className="w-full p-4 flex items-center justify-between hover:bg-gray-800 transition-colors">
        <div className="text-left">
          <h3 className="font-semibold text-sm uppercase tracking-wide text-gray-400">Copy Trading Activation</h3>
          <p className="text-xs text-gray-500 mt-0.5">Subaccount 1877055 &mdash; all 6 conditions must be green</p>
        </div>
        <div className="flex items-center gap-2">
          {loading && <Loader2 size={14} className="text-gray-500 animate-spin" />}
          <span className={`text-xs px-2 py-1 rounded border font-bold ${isActive ? 'bg-green-900 border-green-700 text-green-300' : 'bg-gray-800 border-gray-700 text-gray-500'}`}>
            {isActive ? 'ACTIVE' : 'INACTIVE'}
          </span>
          {open ? <ChevronUp size={16} className="text-gray-500" /> : <ChevronDown size={16} className="text-gray-500" />}
        </div>
      </button>

      {open && <div className="divide-y divide-gray-800 border-t border-gray-800">
        {CONDITION_ORDER.map((key) => {
          const cond = status?.conditions[key];
          if (!cond) return null;
          return (
            <div key={key} className="flex items-center justify-between p-4 hover:bg-gray-800/40 transition-colors">
              <div className="flex items-center gap-3">
                {cond.pass
                  ? <CheckCircle size={18} className="text-green-400 flex-shrink-0" />
                  : <XCircle size={18} className="text-red-400 flex-shrink-0" />}
                <div>
                  <div className="font-medium text-sm">{cond.label}</div>
                  <div className="text-xs text-gray-500 mt-0.5">{conditionValueLabel(key, cond)}</div>
                </div>
              </div>
              <span className={`text-xs font-bold ml-4 ${cond.pass ? 'text-green-400' : 'text-red-400'}`}>
                {cond.pass ? '✓' : '✗'}
              </span>
            </div>
          );
        })}
      </div>}

      {open && <div className="p-4 border-t border-gray-800">
        {allGreen ? (
          <button
            onClick={handleToggle}
            disabled={activating}
            className={`w-full flex items-center justify-center gap-2 py-2 px-4 rounded font-bold text-sm transition-colors ${
              isActive
                ? 'bg-yellow-900 hover:bg-yellow-800 border border-yellow-700 text-yellow-300'
                : 'bg-green-600 hover:bg-green-500 text-white'
            } disabled:opacity-50`}
          >
            {activating && <Loader2 size={14} className="animate-spin" />}
            {isActive ? 'Pause Copy Trading' : 'Activate Copy Trading'}
          </button>
        ) : (
          <button
            disabled
            className="w-full py-2 px-4 rounded font-bold text-sm bg-gray-800 text-gray-600 border border-gray-700 cursor-not-allowed"
          >
            {isActive ? 'Auto-Paused (conditions not met)' : 'Activate Copy Trading (conditions not met)'}
          </button>
        )}
        {!allGreen && isActive && (
          <p className="text-xs text-red-400 mt-2 text-center">
            Conditions no longer met &mdash; copy trading will be auto-paused on next check.
          </p>
        )}
      </div>}
    </div>
  );
}
